// Hand repairs for prefabs authored with a hole in them.
//
// `bridge.ts` closes thin gaps generically, but some units were drawn with whole layers
// missing: a robe that stops at the waist with a hood floating three voxels above it, a wraith
// whose legs hang in the air, a skirt that does not reach the breastplate. Each entry fills
// the empty layers between two parts, each layer taking the footprint (and colour) of the part
// nearer to it, so a chest tapers up into a hood instead of stopping short.
import type { BlockVoxel, CharacterPart } from './types.js';

interface Fill {
  /** The part that receives the new voxels. */
  readonly into: string;
  /** The part below the hole and the part above it, by name. */
  readonly lower: string;
  readonly upper: string;
  /** The empty layers, inclusive. */
  readonly y0: number;
  readonly y1: number;
}

const REPAIRS: Readonly<Record<string, readonly Fill[]>> = {
  // Robe to waist only; the chest between it and the hood was never drawn.
  blood_priest: [{ into: 'robe', lower: 'robe', upper: 'hood', y0: 8, y1: 10 }],
  // Legs hang below a torso with two empty layers between: a trailing ghost tail.
  wraith: [{ into: 'body', lower: 'left_leg', upper: 'body', y0: 4, y1: 5 }],
  banshee: [{ into: 'body', lower: 'left_leg', upper: 'body', y0: 4, y1: 5 }],
  // The skirt stops two layers short of the breastplate.
  luminarch: [{ into: 'robe_skirt', lower: 'robe_skirt', upper: 'torso_armor', y0: 4, y1: 5 }],
};

/** The voxels of `part` in the layer nearest `y` on the given side. */
function nearestLayer(
  part: CharacterPart | undefined,
  y: number,
  side: 'below' | 'above',
): BlockVoxel[] {
  if (!part) return [];
  const layers = [...new Set(part.voxels.map((v) => v.y))].filter((ly) =>
    side === 'below' ? ly < y : ly > y,
  );
  if (layers.length === 0) return [];
  const pick = side === 'below' ? Math.max(...layers) : Math.min(...layers);
  return part.voxels.filter((v) => v.y === pick);
}

export function repairHoles(id: string, parts: readonly CharacterPart[]): CharacterPart[] {
  const fills = REPAIRS[id];
  if (!fills) return [...parts];
  const out = parts.map((p) => ({ ...p, voxels: [...p.voxels] }));
  const find = (name: string) => out.find((p) => p.name === name);
  const taken = new Set(parts.flatMap((p) => p.voxels.map((v) => `${v.x},${v.y},${v.z}`)));
  for (const f of fills) {
    const into = find(f.into);
    if (!into) continue;
    for (let y = f.y0; y <= f.y1; y++) {
      const below = nearestLayer(find(f.lower), y, 'below');
      const above = nearestLayer(find(f.upper), y, 'above');
      // Whichever end is closer sets the shape; ties go to the lower end (the wider one, usually).
      const lowerDist = below.length ? y - (below[0] as BlockVoxel).y : Number.POSITIVE_INFINITY;
      const upperDist = above.length ? (above[0] as BlockVoxel).y - y : Number.POSITIVE_INFINITY;
      const src = lowerDist <= upperDist ? below : above;
      for (const v of src) {
        const key = `${v.x},${y},${v.z}`;
        if (taken.has(key)) continue;
        taken.add(key);
        into.voxels.push({ ...v, y });
      }
    }
  }
  return out;
}
